(function() {
  class Lucrative_ContentDataLayer {
    constructor() {
      window.dataLayer = window.dataLayer || [];

      // use a prefix of events name
      this.eventPrefix = '';

      // how much of a section must be visible before it counts as viewed
      this.sectionVisibleRatio = 0.5;

      // how much of a slider must be visible before its slides count as viewed
      this.sliderVisibleRatio = 0.5;

      // links that count as going to a product page
      this.productLinkSelector = 'a[href*="/products/"]';

      // Sections that report content_view. Any section carrying data-analytics-section
      // (set through its analytics_name setting) is tracked as well, so a new campaign
      // section opts in from the theme editor without a code change.
      this.campaignSections = ['ds-main-hero-component', 'slideshow', 'image-with-text-full', 'image-with-text'];

      // Announcement messages are reported once per session instead of once per page,
      // they are on every page and would otherwise dominate the event volume.
      this.announcementStorageKey = 'lucrative_announcements_seen';

      // Caution: Do not modify anything below this line, as it may result in it not functioning correctly.
      this.seen = new Set();
      this.sectionObserver = null;
      this.promotionObserver = null;
      this.visibleSliders = new WeakSet();

      this.sectionViews();
      this.slideViews();
      this.promotionViews();
      this.clicks();
      this.interactions();
      this.watchForNewSections();
    }

    // ---------------------------------------------------------------- push

    push(event, data) {
      const payload = { event: this.eventPrefix + event };
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          payload[key] = data[key];
        }
      });
      window.dataLayer.push(payload);
    }

    // Fires at most once per name+type+index combination per page load.
    pushOnce(key, event, data) {
      if (this.seen.has(key)) return;
      this.seen.add(key);
      this.push(event, data);
    }

    // Same, but once per session. Falls back to once per page when storage is blocked.
    pushOncePerSession(key, event, data) {
      let stored = [];
      try {
        stored = JSON.parse(window.sessionStorage.getItem(this.announcementStorageKey) || '[]');
      } catch (error) {
        stored = [];
      }
      if (stored.indexOf(key) !== -1) return;
      this.pushOnce(key, event, data);
      try {
        stored.push(key);
        window.sessionStorage.setItem(this.announcementStorageKey, JSON.stringify(stored));
      } catch (error) {}
    }

    // ---------------------------------------------------------------- names

    // Resolve a section name. Explicit attributes win; everything after that
    // is a fallback so that an uninstrumented section is still reported under
    // a stable, human-readable name rather than being dropped.
    sectionName(el) {
      if (!el) return '';

      const explicit = el.closest('[data-analytics-section]');
      if (explicit) return explicit.getAttribute('data-analytics-section');

      const wrapper = el.closest('.shopify-section');
      if (wrapper) {
        // Schema "class": "shopify-section--ds-main-hero-component ..." is the
        // most stable fallback, it survives theme duplication and re-ordering.
        const fromClass = Array.from(wrapper.classList)
          .find((c) => c.indexOf('shopify-section--') === 0);
        if (fromClass) return fromClass.replace('shopify-section--', '');

        const typed = wrapper.querySelector('[data-section-type]');
        if (typed) return typed.getAttribute('data-section-type');

        const fromId = this.nameFromSectionId(wrapper.id);
        if (fromId) return fromId;
      }

      return 'unnamed';
    }

    // shopify-section-header                                    -> header
    // shopify-section-template--123__ds_main_hero_component_AbC1 -> ds_main_hero_component
    // shopify-section-template--123__8f185e9b-daf2-4cfe-...      -> '' (unusable)
    nameFromSectionId(id) {
      if (!id) return '';
      let name = id.replace(/^shopify-section-/, '');
      name = name.replace(/^template--\d+__/, '');
      // The theme editor appends a random 6-7 character suffix when a section
      // is added through the customizer. Strip it so the same section on two
      // different pages reports the same name.
      name = name.replace(/_[A-Za-z0-9]{6,7}$/, '');
      // Section keys that are bare UUIDs or hashes carry no meaning.
      if (/^[0-9a-f-]{16,}$/i.test(name)) return '';
      return name;
    }

    // Text fallback for a clickable that carries no explicit name.
    // Note: this is localised, so DE and FR produce different values for the
    // same control. Set data-analytics-cta on anything you need to compare
    // across stores.
    labelOf(el) {
      if (!el) return '';
      const label = el.getAttribute('aria-label')
        || el.getAttribute('data-item-title')
        || el.textContent
        || '';
      return label.replace(/\s+/g, ' ').trim().slice(0, 100);
    }

    // ---------------------------------------------------- section_view

    sectionViews() {
      if (!('IntersectionObserver' in window)) return;

      this.sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const name = this.sectionName(entry.target);
          this.pushOnce('section:' + name, 'content_view', {
            content_type: 'section',
            content_name: name
          });
          this.sectionObserver.unobserve(entry.target);
        });
      }, { threshold: this.sectionVisibleRatio });

      this.observeSections(document);
    }

    observeSections(root) {
      if (!this.sectionObserver) return;
      root.querySelectorAll('.shopify-section').forEach((el) => {
        if (this.isCampaignSection(el)) this.sectionObserver.observe(el);
      });
    }

    // Layout sections (header, popup, mini cart, ...) are on every page and say nothing
    // about a campaign, so only campaign slots are observed. The announcement bar is
    // reported per message by the slider code, not as a section.
    isCampaignSection(wrapper) {
      if (wrapper.querySelector('announcement-bar')) return false;
      if (wrapper.querySelector('[data-analytics-section]')) return true;
      return this.campaignSections.indexOf(this.sectionName(wrapper)) !== -1;
    }

    // Sections re-rendered through the Section Rendering API are not present
    // at DOMContentLoaded, so pick them up as they arrive.
    watchForNewSections() {
      if (!('MutationObserver' in window)) return;
      new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.classList && node.classList.contains('shopify-section') && this.isCampaignSection(node)) {
              this.sectionObserver && this.sectionObserver.observe(node);
            }
            if (node.querySelectorAll) this.observeSections(node);
            if (node.matches && node.matches('[data-analytics-promotion]')
              && !node.matches('ds-slide-show-item, slide-show-item, announcement-bar-item')) {
              this.promotionObserver && this.promotionObserver.observe(node);
            }
            this.observePromotions(node);
            this.bindSlider(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    // ------------------------------------------------------- slide_view

    // Both ds-slide-show-item and announcement-bar-item signal the active
    // item by toggling the `hidden` attribute, so one mechanism covers the
    // hero slider, every other ds-slide-show, and the announcement bar.
    //
    // Deliberately not hooked into Slideshow.select() in ds-slideshow.js:
    // the theme loads ds-slideshow.min.js and nothing in vite.config.mjs
    // rebuilds it, so a change there would have to be hand-minified and would
    // be lost the next time that component is rebuilt.
    slideViews() {
      if (!('MutationObserver' in window)) return;
      this.bindSlider(document);
    }

    // The theme has two slider components, ds-slide-show and the older slide-show,
    // plus the announcement bar. All three mark the active item the same way, by
    // toggling `hidden`, and all three name their item element <tag>-item.
    bindSlider(root) {
      if (!root.querySelectorAll) return;
      const sliders = root.querySelectorAll('ds-slide-show, slide-show, announcement-bar');
      sliders.forEach((slider) => this.observeSlider(slider));
    }

    observeSlider(slider) {
      if (slider.__lucrativeBound) return;
      slider.__lucrativeBound = true;

      const tag = slider.tagName.toLowerCase();
      const isAnnouncement = tag === 'announcement-bar';
      const itemSelector = tag + '-item';
      const contentType = isAnnouncement ? 'announcement' : 'slide';

      // Gate on the slider being on screen. An autoplaying slider below the
      // fold would otherwise report a view for every slide it cycles through.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.visibleSliders.add(slider);
              const active = slider.querySelector(itemSelector + ':not([hidden])');
              if (active) this.reportSlide(slider, active, contentType);
            } else {
              this.visibleSliders.delete(slider);
            }
          });
        }, { threshold: this.sliderVisibleRatio }).observe(slider);
      } else {
        this.visibleSliders.add(slider);
      }

      new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.attributeName !== 'hidden') return;
          const item = m.target;
          if (item.hasAttribute('hidden')) return;
          if (!this.visibleSliders.has(slider)) return;
          this.reportSlide(slider, item, contentType);
        });
      }).observe(slider, {
        attributes: true,
        attributeFilter: ['hidden'],
        subtree: true
      });
    }

    reportSlide(slider, item, contentType) {
      const sliderEl = slider.closest('[data-analytics-slider]');
      const sliderName = sliderEl
        ? sliderEl.getAttribute('data-analytics-slider')
        : this.sectionName(slider);

      const index = item.getAttribute('data-index')
        || String(Array.prototype.indexOf.call(item.parentNode.children, item));

      const slideName = item.getAttribute('data-analytics-slide')
        || this.labelOf(item.querySelector('h1, h2, h3, .announcement-bar-text'))
        || index;

      const key = contentType + ':' + sliderName + ':' + index;
      const data = {
        content_type: contentType,
        content_name: slideName,
        content_group: sliderName,
        content_index: Number(index)
      };
      if (contentType === 'announcement') {
        this.pushOncePerSession(key + ':' + slideName, 'content_view', data);
      } else {
        this.pushOnce(key, 'content_view', data);
      }

      if (contentType === 'slide' || item.hasAttribute('data-analytics-promotion')) {
        this.viewPromotion(item);
      }
    }

    // ----------------------------------------------------- content_click

    clicks() {
      document.addEventListener('click', (event) => {
        const target = event.target.closest(
          '[data-analytics-cta], a[href], button[data-action], .header__linklist-item'
        );
        if (!target) return;
        // Ignore add to cart, that is add_to_cart in lucrative-datalayer.liquid.
        // Product links are kept: a banner CTA to a product page is exactly the
        // content click worth measuring, and lucrative-datalayer.liquid reports
        // the same click as select_item for the ecommerce side.
        if (target.closest('form[action="/cart/add"]')) return;

        const slide = target.closest('ds-slide-show-item, announcement-bar-item');
        const nav = target.closest('.header__linklist-item, .mega-menu__column, .mobile-menu');

        let contentType = target.getAttribute('data-analytics-type');
        if (!contentType) {
          if (slide) contentType = 'slide';
          else if (nav) contentType = 'nav';
          else if (target.closest('footer, .footer')) contentType = 'footer';
          else if (target.matches('a[href*="wa.me"], a[href*="api.whatsapp.com"]')) contentType = 'support';
          else contentType = 'cta';
        }

        const cta = target.getAttribute('data-analytics-cta');
        const href = target.getAttribute('href');

        this.push('content_click', {
          content_type: contentType,
          content_name: cta || this.labelOf(target),
          content_group: slide
            ? (this.sectionName(slide) || '')
            : this.sectionName(target),
          content_index: slide && slide.getAttribute('data-index') !== null
            ? Number(slide.getAttribute('data-index'))
            : undefined,
          content_cta: cta,
          link_url: href && href.indexOf('#') !== 0 ? href : undefined
        });

        // A click from a promotion slot to a product page is also a select_promotion.
        const slot = this.promotionSlot(target);
        if (slot && target.matches(this.productLinkSelector)) {
          this.reportPromotion('select_promotion', slot, target);
        }
      }, true);
    }

    // ------------------------------------- view_promotion / select_promotion

    // A promotion slot is a hero slide, or any element marked data-analytics-promotion.
    // It is only reported as a promotion when it links to a product page. GA4 wants an
    // item on promotion events, and carrying the promotion fields on that item is what
    // lets GA4 connect the banner to a later add_to_cart and purchase of that product.
    //
    // Items are built by lucrative-datalayer.liquid (window.lucrativeEcommerce) so they
    // share one item_id format with every other ecommerce event.
    promotionSlot(el) {
      return el && el.closest ? el.closest('ds-slide-show-item, slide-show-item, [data-analytics-promotion]') : null;
    }

    promotionLink(slot) {
      if (!slot) return null;
      if (slot.matches && slot.matches(this.productLinkSelector)) return slot;
      return slot.querySelector(this.productLinkSelector);
    }

    promotionFor(slot, link) {
      const slider = slot.closest('[data-analytics-slider]');
      const group = slot.getAttribute('data-analytics-promotion')
        || (slider ? slider.getAttribute('data-analytics-slider') : this.sectionName(slot));
      const index = slot.getAttribute('data-index') || '0';
      const slotName = group + '_' + index;
      const explicit = slot.getAttribute('data-analytics-slide');

      return {
        promotion_id: explicit || slotName,
        promotion_name: explicit || this.labelOf(slot.querySelector('h1, h2, h3')) || slotName,
        creative_name: link ? (link.getAttribute('data-analytics-cta') || this.labelOf(link)) : undefined,
        creative_slot: slotName
      };
    }

    reportPromotion(event, slot, link) {
      const api = window.lucrativeEcommerce;
      if (!api || !slot || !link) return;

      const href = link.getAttribute('href');
      const promotion = this.promotionFor(slot, link);
      const send = (item) => {
        if (!item) return;
        Object.keys(promotion).forEach((key) => {
          if (promotion[key] !== undefined && promotion[key] !== '') item[key] = promotion[key];
        });
        api.push(event, { items: [item] });
      };

      // select_promotion comes from a click that navigates away, so use the cached
      // product when it is there. view_promotion has already fetched it for the
      // slot's own link, so by the time someone clicks it usually is.
      const cached = api.cachedItem(href);
      if (cached) {
        send(cached);
      } else {
        api.productFromLink(href).then(send);
      }
    }

    viewPromotion(slot) {
      const link = this.promotionLink(slot);
      if (!link) return;
      const key = 'promotion:' + this.promotionFor(slot, link).creative_slot;
      if (this.seen.has(key)) return;
      this.seen.add(key);
      this.reportPromotion('view_promotion', slot, link);
    }

    // Promotion slots outside a slider. Slides are reported from reportSlide, when
    // they become the active one, not merely when they scroll into view.
    promotionViews() {
      if (!('IntersectionObserver' in window)) return;
      this.promotionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.viewPromotion(entry.target);
          this.promotionObserver.unobserve(entry.target);
        });
      }, { threshold: this.sectionVisibleRatio });

      this.observePromotions(document);
    }

    observePromotions(root) {
      if (!this.promotionObserver || !root.querySelectorAll) return;
      root.querySelectorAll('[data-analytics-promotion]').forEach((el) => {
        if (el.matches('ds-slide-show-item, slide-show-item, announcement-bar-item')) return;
        this.promotionObserver.observe(el);
      });
    }

    // ----------------------------------------------- content_interaction

    // Reuses the theme's own CustomEvent bus rather than re-detecting these
    // states from the DOM. See assets/theme.js.
    interactions() {
      const self = this;

      // The mega menu opens on hover, so its open is not tracked. Clicks on menu
      // items are reported as content_click with content_type nav.

      document.addEventListener('announcement-bar:content:open', (event) => {
        self.push('content_interaction', {
          content_type: 'announcement',
          content_name: self.labelOf(event.target.querySelector('.announcement-bar-text')),
          interaction: 'open'
        });
      }, true);

      // FAQ accordions are native <details> elements in this theme.
      document.addEventListener('toggle', (event) => {
        const details = event.target;
        if (!details.matches || !details.matches('details')) return;
        if (!details.open) return;
        const summary = details.querySelector('summary');
        const siblings = details.parentNode
          ? Array.prototype.filter.call(details.parentNode.children, (c) => c.tagName === 'DETAILS')
          : [];
        self.push('content_interaction', {
          content_type: 'faq',
          content_name: self.labelOf(summary),
          content_group: self.sectionName(details),
          content_index: siblings.indexOf(details),
          interaction: 'open'
        });
      }, true);

      // Slider next / previous / dots. The view itself is reported by the
      // hidden-attribute observer, this only records the intent.
      document.addEventListener('click', (event) => {
        const control = event.target.closest('[data-action="prev"], [data-action="next"], ds-page-dots button');
        if (!control) return;
        const slider = control.closest('ds-slide-show, announcement-bar');
        if (!slider) return;
        const action = control.getAttribute('data-action') || 'dot';
        self.push('content_interaction', {
          content_type: 'slider',
          content_name: self.sectionName(slider),
          interaction: action
        });
      }, true);
    }
  }
  // end Lucrative_ContentDataLayer

  document.addEventListener('DOMContentLoaded', function() {
    try {
      new Lucrative_ContentDataLayer();
    } catch (error) {
      console.error(error);
    }
  });

})();
